import { Component, forwardRef, Input, OnInit, Output, ViewChild, ElementRef, HostListener, AfterViewInit, OnDestroy } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { IonCol } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import moment, { Moment } from 'moment';
import { AccessibilityFocusService } from 'src/app/services/accessibility.service';
import { TimeUtils } from 'src/smoothr-web-app-core/utils/time-utils';

const SEARCH_INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => SelectDateFormcontrolComponent),
	multi: false,
};

@Component({
	selector: 'app-select-date-formcontrol',
	templateUrl: './select-date-formcontrol.component.html',
	styleUrls: ['./select-date-formcontrol.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SelectDateFormcontrolComponent),
			multi: true,
		},
	],
})
export class SelectDateFormcontrolComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy {
	@ViewChild('timeCol', { static: true }) timeCol: IonCol;

	// Accessibility properties
	private currentDateIndex: number = 0;
	private currentTimeIndex: number = 0;
	private isInitialized: boolean = false;

	@Input() set slots(slots: Moment[]) {
		this.dates = [];
		this._slots = slots;
		this._slots.forEach(slot => {
			if (!slot) {
				this.onChange(null);
				return;
			}
			const insertedDate = this.dates.find(date => TimeUtils.isSameDate(date, slot));
			if (!insertedDate) {
				this.dates.push(slot);
			}
		});

		// Update indices when slots change
		if (this.isInitialized) {
			this.updateCurrentIndices();
		}
	}

	onChange: any = () => {};
	onTouched: any = () => {};

	get slots(): Moment[] {
		return this._slots;
	}
	private _slots: Moment[] = [];

	@Input()
	set slot(slot: Moment) {
		this._slot = slot;
		this.onChange(slot);

		// Update indices when slot changes
		if (this.isInitialized) {
			this.updateCurrentIndices();
		}
	}

	get slot(): Moment {
		return this._slot;
	}
	private _slot: Moment;

	dates: Moment[] = [];
	now: moment.Moment;
	maxSlots = 5;
	maxDates = 3;
	TimeUtils = TimeUtils;

	constructor(
		private translate: TranslateService,
		private elementRef: ElementRef,
		private a11yService: AccessibilityFocusService
	) {
		this.now = moment();
	}

	ngOnInit(): void {
		this.isInitialized = true;
		this.updateCurrentIndices();
	}

	ngAfterViewInit(): void {
		// Announce component initialization to screen readers
		setTimeout(() => {
			if (this.dates.length > 0) {
				this.a11yService.announceToScreenReader(`Date and time selector loaded with ${this.dates.length} available dates`);
			} else {
				this.a11yService.announceToScreenReader('Date and time selector loaded. No dates available.');
			}
		}, 500);
	}

	ngOnDestroy(): void {
		// Clean up any subscriptions or listeners if needed
	}

	writeValue(value: any): void {
		if (value) {
			this._slot = moment.isMoment(value) ? value : moment(value);
			this.updateCurrentIndices();
		}
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	/**
	 * Convert slot to display text
	 */
	slotToText(slot: Moment): string {
		if (!slot) {
			return null;
		}
		if (!moment.isMoment(slot)) {
			slot = moment(slot);
		}
		const now = moment();
		const sameDay = TimeUtils.isSameDate(slot, now);
		if (sameDay) {
			return slot.format('HH:mm');
		} else {
			return slot.format('HH:mm / DD.MM.yyyy');
		}
	}

	/**
	 * Convert slot to date text for display
	 */
	slotToDateText(slot: Moment): string {
		if (TimeUtils.isSameDate(slot, this.now)) {
			return this.translate.instant('today');
		}
		return slot.locale(this.translate.currentLang).format('dd DD.MM.');
	}

	/**
	 * Convert slot to time text for display
	 */
	slotToTimeText(slot: Moment): string {
		if (!slot) return this.translate.instant('checkout_modal.first_slot');
		return slot.format('HH:mm');
	}

	/**
	 * Get available times for a specific date
	 */
	timesForDate(selectedDate: Moment): Moment[] {
		const date = selectedDate ?? this.dates[0] ?? null;
		if (!date) {
			return [];
		}
		return this.slots.filter(slot => TimeUtils.isSameDate(slot, date) || (!slot && date === this.dates[0]));
	}

	/**
	 * Set the selected date with accessibility enhancements
	 */
	setDate(date: Moment): void {
		if (!date) return;

		this.slot = date;
		this.maxSlots = 5;
		this.onTouched();
		this.scrollTimeToTop();

		// Announce selection to screen readers
		const dateText = this.slotToDateText(date);
		this.a11yService.announceToScreenReader(`Date selected: ${dateText}`);

		// Update selection announcer
		this.updateSelectionAnnouncer(`Date selected: ${dateText}`);
	}

	/**
	 * Set the selected time with accessibility enhancements
	 */
	setTime(time: Moment): void {
		if (!time) return;

		this.slot = time;
		this.onTouched();

		// Announce selection to screen readers
		const timeText = this.slotToTimeText(time);
		this.a11yService.announceToScreenReader(`Time selected: ${timeText}`);

		// Update selection announcer
		this.updateSelectionAnnouncer(`Time selected: ${timeText}`);
	}

	/**
	 * Show more dates with accessibility announcement
	 */
	showMoreDates(): void {
		this.maxDates = this.maxDates + 3;
		this.a11yService.announceToScreenReader(
			`Showing more dates. ${Math.min(this.maxDates, this.dates.length)} of ${this.dates.length} dates visible.`
		);
	}

	/**
	 * Show more time slots with accessibility announcement
	 */
	showMoreTimes(): void {
		this.maxSlots = this.maxSlots + 5;
		const availableTimes = this.timesForDate(this.slot);
		this.a11yService.announceToScreenReader(
			`Showing more times. ${Math.min(this.maxSlots, availableTimes.length)} of ${availableTimes.length} times visible.`
		);
	}

	/**
	 * Scroll time column to top
	 */
	async scrollTimeToTop(): Promise<void> {
		try {
			// @ts-ignore
			const el: HTMLDivElement = this.timeCol.el;
			el.scrollTo({
				left: 0,
				top: 0,
				behavior: 'smooth',
			});
		} catch (error) {
			console.warn('Error scrolling time column:', error);
		}
	}

	/**
	 * Get ARIA label for date button
	 */
	getDateAriaLabel(date: Moment, index: number): string {
		const dateText = this.slotToDateText(date);
		const isSelected = this.TimeUtils.isSameDate(date, this.slot) || (index === 0 && !this.slot);
		const selectedText = isSelected ? ', selected' : '';
		const todayText = TimeUtils.isSameDate(date, this.now) ? ', today' : '';

		return `${dateText}${todayText}${selectedText}`;
	}

	/**
	 * Get ARIA label for time button
	 */
	getTimeAriaLabel(time: Moment, index: number): string {
		if (!time) return this.translate.instant('checkout_modal.first_slot');

		const timeText = this.slotToTimeText(time);
		const isSelected = this.slot === time;
		const selectedText = isSelected ? ', selected' : '';

		return `${timeText}${selectedText}`;
	}

	/**
	 * Get ID of currently selected date
	 */
	getSelectedDateId(): string {
		const selectedIndex = this.dates.findIndex(date => this.TimeUtils.isSameDate(date, this.slot));
		return selectedIndex >= 0 ? `date-${selectedIndex}` : 'date-0';
	}

	/**
	 * Get ID of currently selected time
	 */
	getSelectedTimeId(): string {
		const times = this.timesForDate(this.slot);
		const selectedIndex = times.findIndex(time => this.slot === time);
		return selectedIndex >= 0 ? `time-${selectedIndex}` : '';
	}

	/**
	 * Handle keyboard navigation for date selection
	 */
	handleDateKeyNavigation(event: KeyboardEvent): void {
		const visibleDates = this.dates.slice(0, this.maxDates);

		switch (event.key) {
			case 'ArrowDown':
			case 'ArrowRight':
				event.preventDefault();
				this.currentDateIndex = Math.min(this.currentDateIndex + 1, visibleDates.length - 1);
				this.focusDateOption(this.currentDateIndex);
				break;

			case 'ArrowUp':
			case 'ArrowLeft':
				event.preventDefault();
				this.currentDateIndex = Math.max(this.currentDateIndex - 1, 0);
				this.focusDateOption(this.currentDateIndex);
				break;

			case 'Home':
				event.preventDefault();
				this.currentDateIndex = 0;
				this.focusDateOption(this.currentDateIndex);
				break;

			case 'End':
				event.preventDefault();
				this.currentDateIndex = visibleDates.length - 1;
				this.focusDateOption(this.currentDateIndex);
				break;

			case 'Enter':
			case ' ':
				event.preventDefault();
				if (visibleDates[this.currentDateIndex]) {
					this.setDate(visibleDates[this.currentDateIndex]);
				}
				break;
		}
	}

	/**
	 * Handle keyboard navigation for time selection
	 */
	handleTimeKeyNavigation(event: KeyboardEvent): void {
		const visibleTimes = this.timesForDate(this.slot).slice(0, this.maxSlots);

		switch (event.key) {
			case 'ArrowDown':
			case 'ArrowRight':
				event.preventDefault();
				this.currentTimeIndex = Math.min(this.currentTimeIndex + 1, visibleTimes.length - 1);
				this.focusTimeOption(this.currentTimeIndex);
				break;

			case 'ArrowUp':
			case 'ArrowLeft':
				event.preventDefault();
				this.currentTimeIndex = Math.max(this.currentTimeIndex - 1, 0);
				this.focusTimeOption(this.currentTimeIndex);
				break;

			case 'Home':
				event.preventDefault();
				this.currentTimeIndex = 0;
				this.focusTimeOption(this.currentTimeIndex);
				break;

			case 'End':
				event.preventDefault();
				this.currentTimeIndex = visibleTimes.length - 1;
				this.focusTimeOption(this.currentTimeIndex);
				break;

			case 'Enter':
			case ' ':
				event.preventDefault();
				if (visibleTimes[this.currentTimeIndex]) {
					this.setTime(visibleTimes[this.currentTimeIndex]);
				}
				break;
		}
	}

	/**
	 * Focus a specific date option
	 */
	private focusDateOption(index: number): void {
		const dateButton = this.elementRef.nativeElement.querySelector(`#date-${index}`);
		if (dateButton) {
			dateButton.focus();
		}
	}

	/**
	 * Focus a specific time option
	 */
	private focusTimeOption(index: number): void {
		const timeButton = this.elementRef.nativeElement.querySelector(`#time-${index}`);
		if (timeButton) {
			timeButton.focus();
		}
	}

	/**
	 * Update current indices based on selection
	 */
	private updateCurrentIndices(): void {
		// Update date index
		if (this.slot) {
			this.currentDateIndex = this.dates.findIndex(date => this.TimeUtils.isSameDate(date, this.slot));
			if (this.currentDateIndex < 0) this.currentDateIndex = 0;
		}

		// Update time index
		if (this.slot) {
			const times = this.timesForDate(this.slot);
			this.currentTimeIndex = times.findIndex(time => this.slot === time);
			if (this.currentTimeIndex < 0) this.currentTimeIndex = 0;
		}
	}

	/**
	 * Update selection announcer for screen readers
	 */
	private updateSelectionAnnouncer(message: string): void {
		const announcer = this.elementRef.nativeElement.querySelector('#selection-announcer');
		if (announcer) {
			announcer.textContent = message;

			// Clear after announcement
			setTimeout(() => {
				announcer.textContent = '';
			}, 3000);
		}
	}

	/**
	 * Global keyboard event handler for component-wide shortcuts
	 */
	@HostListener('keydown', ['$event'])
	handleGlobalKeydown(event: KeyboardEvent): void {
		// Allow normal keyboard behavior for input elements
		const target = event.target as HTMLElement;
		if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
			return;
		}

		// Handle global shortcuts
		switch (event.key) {
			case 'Tab':
				// Let tab work normally but ensure proper focus management
				break;

			case 'Escape':
				// Clear selection or close component if needed
				// Implement based on your UX requirements
				break;
		}
	}
}
